/**
 * Complex Result Parser
 * 
 * Handles parsing of complex contract return values including:
 * - Structs with dynamic arrays
 * - Nested tuples
 * - BigNumbers
 * - Mixed types
 */

import { ethers } from 'ethers';
import logger from './logger';

export interface ParsedResult {
    formatted: string;
    structured?: any;
    displayType: 'simple' | 'struct' | 'array' | 'burst';
}

/**
 * Main parser function - intelligently handles any contract return value
 */
export const parseComplexResult = (
    result: any,
    functionName: string,
    abi: any[]
): ParsedResult => {
    try {
        // Find the function in ABI
        const functionAbi = abi.find(
            (item: any) => item.type === 'function' && item.name === functionName
        );

        if (!functionAbi) {
            logger.warn(`Function ${functionName} not found in ABI`);
            return {
                formatted: String(result),
                displayType: 'simple'
            };
        }

        // Get output types
        const outputs = functionAbi.outputs || [];

        // Special handling for known problematic functions
        if (functionName === 'getActiveBurst') {
            return parseGetActiveBurst(result);
        }

        if (functionName === 'getBurstHistory') {
            return parseGetBurstHistory(result);
        }

        if (functionName === 'getBurstStatistics') {
            return parseGetBurstStatistics(result);
        }

        // Generic handling based on output types
        if (outputs.length === 0) {
            return { formatted: 'Success (no return value)', displayType: 'simple' };
        }

        if (outputs.length === 1) {
            return parseSingleOutput(result, outputs[0]);
        }

        // Multiple outputs - parse as tuple
        return parseMultipleOutputs(result, outputs);

    } catch (error) {
        logger.error('Error parsing result:', error);
        return {
            formatted: `Error parsing result: ${error instanceof Error ? error.message : String(error)}`,
            displayType: 'simple'
        };
    }
};

/**
 * Parse getActiveBurst() return value
 * Returns: (BurstEvent memory, bool inActiveBurst)
 */
const parseGetActiveBurst = (result: any): ParsedResult => {
    try {
        // Result is an array: [BurstEvent, bool]
        const burstEvent = result[0];
        const inActiveBurst = result[1];

        logger.log('Parsing getActiveBurst result:', {
            burstEvent,
            inActiveBurst,
            transactionTimesLength: burstEvent?.transactionTimes?.length,
            transactionAmountsLength: burstEvent?.transactionAmounts?.length
        });

        // Extract burst data
        const transactionTimes = burstEvent.transactionTimes || [];
        const transactionAmounts = burstEvent.transactionAmounts || [];
        const startTime = burstEvent.startTime ? ethers.BigNumber.from(burstEvent.startTime) : ethers.BigNumber.from(0);
        const endTime = burstEvent.endTime ? ethers.BigNumber.from(burstEvent.endTime) : ethers.BigNumber.from(0);
        const totalVolumeUSD = burstEvent.totalVolumeUSD ? ethers.BigNumber.from(burstEvent.totalVolumeUSD) : ethers.BigNumber.from(0);
        const isComplete = burstEvent.isComplete || false;

        // Format for display
        const formatted = inActiveBurst
            ? `🔥 ACTIVE BURST\n` +
            `Transactions: ${transactionTimes.length}\n` +
            `Total Volume: $${ethers.utils.formatUnits(totalVolumeUSD, 18)} USD\n` +
            `Started: ${startTime.gt(0) ? new Date(startTime.toNumber() * 1000).toLocaleString() : 'N/A'}\n` +
            `Status: ${isComplete ? 'Completed' : 'Active'}`
            : 'No active burst';

        // Return structured data for detailed display
        return {
            formatted,
            structured: {
                inActiveBurst,
                transactionTimes: transactionTimes.map((t: any) => ethers.BigNumber.from(t).toNumber()),
                transactionAmounts: transactionAmounts.map((a: any) =>
                    parseFloat(ethers.utils.formatUnits(ethers.BigNumber.from(a), 18))
                ),
                startTime: startTime.toNumber(),
                endTime: endTime.toNumber(),
                totalVolumeUSD: parseFloat(ethers.utils.formatUnits(totalVolumeUSD, 18)),
                isComplete
            },
            displayType: 'burst'
        };

    } catch (error) {
        logger.error('Error parsing getActiveBurst:', error);
        return {
            formatted: 'Error parsing burst data',
            displayType: 'simple'
        };
    }
};

/**
 * Parse getBurstHistory() return value
 * Returns: BurstEvent[10]
 */
const parseGetBurstHistory = (result: any): ParsedResult => {
    try {
        const bursts = result || [];
        const validBursts = bursts.filter((burst: any) => {
            const times = burst.transactionTimes || [];
            return times.length > 0;
        });

        if (validBursts.length === 0) {
            return {
                formatted: 'No completed bursts',
                displayType: 'simple'
            };
        }

        const formatted = `${validBursts.length} completed burst(s):\n` +
            validBursts.map((burst: any, i: number) => {
                const times = burst.transactionTimes || [];
                const totalVolumeUSD = burst.totalVolumeUSD ? ethers.BigNumber.from(burst.totalVolumeUSD) : ethers.BigNumber.from(0);
                return `${i + 1}. ${times.length} transactions, $${ethers.utils.formatUnits(totalVolumeUSD, 18)} USD`;
            }).join('\n');

        return {
            formatted,
            structured: validBursts,
            displayType: 'array'
        };

    } catch (error) {
        logger.error('Error parsing getBurstHistory:', error);
        return {
            formatted: 'Error parsing burst history',
            displayType: 'simple'
        };
    }
};

/**
 * Parse getBurstStatistics() return value
 * Returns: BurstStatistics struct
 */
const parseGetBurstStatistics = (result: any): ParsedResult => {
    try {
        const stats = result;

        const totalBursts = stats.totalBursts ? ethers.BigNumber.from(stats.totalBursts).toNumber() : 0;
        const totalTransactions = stats.totalTransactions ? ethers.BigNumber.from(stats.totalTransactions).toNumber() : 0;
        const totalVolumeUSD = stats.totalVolumeUSD ? ethers.BigNumber.from(stats.totalVolumeUSD) : ethers.BigNumber.from(0);
        const averageBurstLength = stats.averageBurstLength ? ethers.BigNumber.from(stats.averageBurstLength).toNumber() : 0;
        const lastBurstTime = stats.lastBurstTime ? ethers.BigNumber.from(stats.lastBurstTime).toNumber() : 0;

        if (totalBursts === 0) {
            return {
                formatted: 'No burst statistics (no completed bursts)',
                displayType: 'simple'
            };
        }

        const formatted = `Total Bursts: ${totalBursts}\n` +
            `Total Transactions: ${totalTransactions}\n` +
            `Total Volume: $${ethers.utils.formatUnits(totalVolumeUSD, 18)} USD\n` +
            `Average Length: ${averageBurstLength} transactions\n` +
            `Last Burst: ${lastBurstTime > 0 ? new Date(lastBurstTime * 1000).toLocaleString() : 'N/A'}`;

        return {
            formatted,
            structured: {
                totalBursts,
                totalTransactions,
                totalVolumeUSD: parseFloat(ethers.utils.formatUnits(totalVolumeUSD, 18)),
                averageBurstLength,
                lastBurstTime
            },
            displayType: 'struct'
        };

    } catch (error) {
        logger.error('Error parsing getBurstStatistics:', error);
        return {
            formatted: 'Error parsing burst statistics',
            displayType: 'simple'
        };
    }
};

/**
 * Check if a field name suggests it's a timestamp
 */
const isTimestampField = (name: string): boolean => {
    const timestampKeywords = [
        'timestamp', 'lastupdate', 'createdat', 'updatedat', 'expiry', 'deadline',
        'holdingstart', 'contiguousholding', 'lasttransaction', 'starttime', 'endtime',
        'burststart', 'lasttiereval', 'dailystart', 'windowstart', 'lockendtime',
        'firstunlocktime', 'secondunlocktime', 'unlocktime'
    ];
    const lowerName = name.toLowerCase().replace(/[_\s]/g, '');

    // Check for exact keyword matches
    if (timestampKeywords.some(keyword => lowerName.includes(keyword))) {
        return true;
    }

    // Check if name ends with 'time' or 'date' (but not duration-related)
    const endsWithTimeOrDate = lowerName.match(/(time|date)$/);
    const isDuration = lowerName.includes('duration') || lowerName.includes('period') ||
        lowerName.includes('interval') || lowerName.includes('delay');

    return endsWithTimeOrDate !== null && !isDuration;
};

/**
 * Check if a field name is a duration (not a timestamp)
 */
const isDurationField = (name: string): boolean => {
    const durationKeywords = ['timeincurrent', 'cooldown', 'remaining', 'duration', 'period'];
    const lowerName = name.toLowerCase().replace(/[_\s]/g, '');
    return durationKeywords.some(keyword => lowerName.includes(keyword));
};

/**
 * Check if a field name suggests it's a token ID (NFT ID)
 */
const isTokenIdField = (name: string): boolean => {
    const tokenIdKeywords = ['tokenid', 'nftid', 'positionid', 'id'];
    const lowerName = name.toLowerCase().replace(/[_\s]/g, '');
    return tokenIdKeywords.some(keyword => lowerName === keyword || lowerName.endsWith(keyword));
};

/**
 * Check if a field name suggests it's a percentage/basis points
 */
const isPercentageField = (name: string): boolean => {
    const percentageKeywords = ['percentage', 'percent', 'bps', 'basispoints', 'rate'];
    const lowerName = name.toLowerCase().replace(/[_\s]/g, '');
    return percentageKeywords.some(keyword => lowerName.includes(keyword));
};

/**
 * Check if a field name suggests it's an integer (not 18-decimal USD amount)
 * These fields should use Number() not formatUnits()
 */
const isIntegerField = (name: string): boolean => {
    const integerKeywords = [
        'score', 'threshold', 'tier', 'days', 'daysheld', 'count', 'length',
        'index', 'level', 'increase', 'decrease', 'cooldown', 'duration',
        'maxtier', 'currenttier', 'estimateddays', 'bursts', 'transactions',
        'assetindex'
    ];
    const lowerName = name.toLowerCase().replace(/[_\s]/g, '');
    return integerKeywords.some(keyword => lowerName.includes(keyword));
};

/**
 * Parse single output value
 */
const parseSingleOutput = (result: any, outputAbi: any): ParsedResult => {
    const type = outputAbi.type;
    const fieldName = outputAbi.name || '';

    // Handle BigNumber
    if (ethers.BigNumber.isBigNumber(result)) {
        // Check if this is a timestamp field - DON'T divide by 1e18
        if (isTimestampField(fieldName)) {
            const timestamp = result.toNumber();
            const formatted = timestamp > 0
                ? new Date(timestamp * 1000).toLocaleString()
                : '0 (Never)';
            return {
                formatted,
                displayType: 'simple'
            };
        }

        // Check if this is a token ID - show as raw number
        if (isTokenIdField(fieldName)) {
            return {
                formatted: result.toString(),
                displayType: 'simple'
            };
        }

        // Check if this is a percentage - show as percentage
        if (isPercentageField(fieldName)) {
            const bps = result.toNumber();
            const percentage = bps / 100;
            return {
                formatted: `${percentage}%`,
                displayType: 'simple'
            };
        }

        // Check if this is a duration field (seconds, not timestamp)
        if (isDurationField(fieldName)) {
            return {
                formatted: `${result.toNumber()} seconds`,
                displayType: 'simple'
            };
        }

        // Check if this is an integer field
        if (isIntegerField(fieldName)) {
            return {
                formatted: result.toNumber().toString(),
                displayType: 'simple'
            };
        }

        // Regular number - format with decimals
        const formatted = ethers.utils.formatUnits(result, 18);
        return {
            formatted,
            displayType: 'simple'
        };
    }

    // Handle arrays
    if (type.includes('[]')) {
        return parseArray(result, type);
    }

    // Handle tuples (structs)
    if (type === 'tuple' && outputAbi.components) {
        return parseStruct(result, outputAbi.components);
    }

    // Simple types
    return {
        formatted: String(result),
        displayType: 'simple'
    };
};

/**
 * Parse multiple outputs (tuple)
 */
const parseMultipleOutputs = (result: any, outputs: any[]): ParsedResult => {
    try {
        const formatted = outputs.map((output, i) => {
            const value = result[i];
            const name = output.name || `output${i}`;

            if (ethers.BigNumber.isBigNumber(value)) {
                // Check if this is a timestamp field
                if (isTimestampField(name)) {
                    const timestamp = value.toNumber();
                    const formattedTime = timestamp > 0
                        ? new Date(timestamp * 1000).toLocaleString()
                        : '0 (Never)';
                    return `${name}: ${formattedTime}`;
                }
                // Check if this is a token ID
                if (isTokenIdField(name)) {
                    return `${name}: ${value.toString()}`;
                }
                // Check if this is a percentage
                if (isPercentageField(name)) {
                    const bps = value.toNumber();
                    const percentage = bps / 100;
                    return `${name}: ${percentage}%`;
                }
                // Check if this is an integer field (scores, tiers, counts)
                if (isIntegerField(name)) {
                    return `${name}: ${value.toNumber()}`;
                }
                // Regular number - format with decimals (USD amounts, etc)
                return `${name}: ${ethers.utils.formatUnits(value, 18)}`;
            }

            return `${name}: ${String(value)}`;
        }).join('\n');

        return {
            formatted,
            displayType: 'struct'
        };

    } catch (error) {
        logger.error('Error parsing multiple outputs:', error);
        return {
            formatted: String(result),
            displayType: 'simple'
        };
    }
};

/**
 * Parse array values
 */
const parseArray = (result: any, type: string): ParsedResult => {
    try {
        if (!Array.isArray(result)) {
            return { formatted: String(result), displayType: 'simple' };
        }

        if (result.length === 0) {
            return { formatted: '[] (empty array)', displayType: 'array' };
        }

        // Check if array elements are BigNumbers
        if (ethers.BigNumber.isBigNumber(result[0])) {
            const formatted = result.map((item: any, i: number) =>
                `[${i}]: ${ethers.utils.formatUnits(item, 18)}`
            ).join('\n');

            return {
                formatted,
                structured: result.map((item: any) => parseFloat(ethers.utils.formatUnits(item, 18))),
                displayType: 'array'
            };
        }

        // Check if array elements are addresses
        if (type.includes('address')) {
            const formatted = result.map((item: any, i: number) =>
                `[${i}]: ${item}`
            ).join('\n');

            return {
                formatted,
                structured: result,
                displayType: 'array'
            };
        }

        // Generic array
        const formatted = result.map((item: any, i: number) =>
            `[${i}]: ${String(item)}`
        ).join('\n');

        return {
            formatted,
            structured: result,
            displayType: 'array'
        };

    } catch (error) {
        logger.error('Error parsing array:', error);
        return {
            formatted: String(result),
            displayType: 'simple'
        };
    }
};

/**
 * Parse struct (tuple) values
 */
const parseStruct = (result: any, components: any[]): ParsedResult => {
    try {
        const formatted = components.map((comp, i) => {
            const value = result[i];
            const name = comp.name || `field${i}`;

            if (ethers.BigNumber.isBigNumber(value)) {
                // Check if this is a timestamp field
                if (isTimestampField(name)) {
                    const timestamp = value.toNumber();
                    const formattedTime = timestamp > 0
                        ? new Date(timestamp * 1000).toLocaleString()
                        : '0 (Never)';
                    return `${name}: ${formattedTime}`;
                }
                // Check if this is a token ID
                if (isTokenIdField(name)) {
                    return `${name}: ${value.toString()}`;
                }
                // Check if this is a percentage
                if (isPercentageField(name)) {
                    const bps = value.toNumber();
                    const percentage = bps / 100;
                    return `${name}: ${percentage}%`;
                }
                // Check if this is an integer field (scores, tiers, counts)
                if (isIntegerField(name)) {
                    return `${name}: ${value.toNumber()}`;
                }
                // Regular number - format with decimals (USD amounts, etc)
                return `${name}: ${ethers.utils.formatUnits(value, 18)}`;
            }

            if (Array.isArray(value)) {
                return `${name}: [${value.length} items]`;
            }

            return `${name}: ${String(value)}`;
        }).join('\n');

        return {
            formatted,
            displayType: 'struct'
        };

    } catch (error) {
        logger.error('Error parsing struct:', error);
        return {
            formatted: String(result),
            displayType: 'simple'
        };
    }
};
